<template>
  <highcharts
    class="humid-chart"
    :options="tempChartOption"
  />
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import convertToCelsius from '@shared/lib/converter/convertToCelsius';

export default {
  name: 'HumidityGraph',
  mixins: [unitPreferencesMixin],
  props: {
    graphData: Array,
  },
  computed: {
    ...mapState({
      sensors: state => state.customerDetail.sensors,
    }),
    humidityGraphData() {
      const outsideAirTemp = [];
      const roomTemp = [];
      const roomHumid = [];
      const cycleDate = [];
      const temp = {};
      let humidityMax = 40;
      this.sensors.forEach((s) => {
        if (s.sensorType === 'A2TempHumidity' || s.sensorType === 'A2RoomTempHumidity') {
          temp[s.serialNumber] = s.sensorType;
        }
      });
      this.graphData.forEach((r) => {
        const {
          supplyAir,
          rooms,
          tempAPI,
          startTime,
        } = r;
        if (rooms) {
          const roomSerials = Object.keys(rooms);
          const roomSerial = roomSerials[0];
          if (roomSerial in temp) {
            const theRoom = rooms[roomSerial];
            let temp = theRoom.temps.avg;
            if (this.tempFormat === '°C') {
              temp = convertToCelsius(temp);
            }
            roomTemp.push(theRoom.temps.avg);
            roomHumid.push(theRoom.humidity.avg);
            humidityMax = theRoom.humidity.max;
          }
        } else if (supplyAir?.humidity) {
          let temp = supplyAir.avgTemp;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(temp);
          }
          roomTemp.push(temp);
          roomHumid.push(supplyAir.humidity.avg);
          humidityMax = supplyAir.humidity.max;
        }
        if (tempAPI !== null && tempAPI !== undefined) {
          let temp = tempAPI;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(tempAPI);
          }
          outsideAirTemp.push(temp);
        }
        if (startTime) {
          let date = moment(startTime).format('M/D/YY h:mm:ss a');
          if (this.dateFormat === 'D/M') {
            date = moment(startTime).format('D/M/YY h:mm:ss a');
          }
          cycleDate.unshift(date);
        }
      });
      return {
        outsideAirTemp,
        roomTemp,
        roomHumid,
        cycleDate,
      };
    },
    tempChartOption() {
      const {
        tempFormat,
        dateFormat,
      } = this;
      const {
        outsideAirTemp,
        roomTemp,
        roomHumid,
        cycleDate,
      } = this.humidityGraphData;

      return {
        title: '',
        chart: {
          type: 'line',
          zoomType: 'x',
          height: 600,
          spacingTop: 50,
        },
        credits: {
          enabled: false,
        },
        yAxis: [{
          labels: {
            // format: '{value}°F',
          },
          title: {
            text: 'Humidity',
          },
          min: 0,
          max: 100,
        }, {
          labels: {
            format: `{value}${tempFormat}`,
          },
          title: {
            text: 'Temperature',
          },
          min: Math.min(...outsideAirTemp, ...roomTemp) - 4,
          max: Math.max(...outsideAirTemp, ...roomTemp) + 4,
          opposite: true,
        }],
        xAxis: [{
          categories: cycleDate,
        }],
        legend: {
          layout: 'vertical',
          align: 'right',
          verticalAlign: 'middle',
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true,
            },
            connectNulls: true,
            marker: {
              states: {
                select: {
                  lineColor: 'rgba(100,100,200, 0.25)',
                  fillColor: 'rgba(223, 83, 83, 1)',
                  lineWidth: 10,
                  radius: 6,
                },
              },
            },
          },
        },
        series: [{
          name: 'OA Temp',
          color: 'rgba(119, 152, 191, 1)',
          data: outsideAirTemp,
          yAxis: 1,
        }, {
          name: 'Room Temp',
          color: 'rgba(223, 83, 83, 1)',
          data: roomTemp,
          yAxis: 1,
        }, {
          name: 'Room Humidity',
          color: 'rgba(240, 240, 0, 1)',
          data: roomHumid,
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 2000,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
              },
            },
          }],
        },
      };
    },
  },
  watch: {
    graphData() {
      const supplyTemp = [];
      const returnTemp = [];
      const atticTemp = [];
      const deltaTemp = [];
      const deltaAvgTemp = [];
      const oaTemp = [];
      const cycleDate = [];
      let sensorTempMin = null;
      let sensorTempMax = null;
      let oaTempMin = null;
      let oaTempMax = null;

      this.graphData.forEach((c) => {
        const {
          supplyAir,
          returnAir,
          atticAir,
          acMode,
          deltaT,
          dailyStat,
          startTime,
          tempAPI,
        } = c;

        if (tempAPI) {
          oaTemp.unshift(tempAPI);
          if (!oaTempMin) {
            oaTempMin = tempAPI;
          } else if (oaTempMin > tempAPI) {
            oaTempMin = tempAPI;
          }
          if (!oaTempMax) {
            oaTempMax = tempAPI;
          } else if (oaTempMax < tempAPI) {
            oaTempMax = tempAPI;
          }
        } else {
          oaTemp.unshift(null);
        }

        if (acMode === 'Heat') {
          if (supplyAir && supplyAir.minTemp > 32) {
            const temp = Math.round(supplyAir.maxTemp);
            supplyTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            supplyTemp.unshift(null);
          }
          if (returnAir && returnAir.minTemp > 32) {
            const temp = Math.round(returnAir.maxTemp);
            returnTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            returnTemp.unshift(null);
          }
        } else if (acMode === 'Cool') {
          if (supplyAir && supplyAir.minTemp > 32) {
            const temp = Math.round(supplyAir.minTemp);
            supplyTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            supplyTemp.unshift(null);
          }
          if (returnAir && returnAir.minTemp > 32) {
            const temp = Math.round(returnAir.minTemp);
            returnTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            returnTemp.unshift(null);
          }
        } else {
          if (supplyAir && supplyAir.endTemp > 32) {
            const temp = Math.round(supplyAir.endTemp);
            supplyTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            supplyTemp.unshift(null);
          }
          if (returnAir && returnAir.endTemp > 32) {
            const temp = Math.round(returnAir.endTemp);
            returnTemp.unshift(temp);
            if (!sensorTempMin) {
              sensorTempMin = temp;
            } else if (sensorTempMin > temp) {
              sensorTempMin = temp;
            }
            if (!sensorTempMax) {
              sensorTempMax = temp;
            } else if (sensorTempMax < temp) {
              sensorTempMax = temp;
            }
          } else {
            returnTemp.unshift(null);
          }
        }

        if (supplyAir && returnAir
          && supplyAir.minTemp <= 32
          && returnAir.minTemp <= 32) {
          deltaTemp.unshift(null);
        } else {
          deltaTemp.unshift(Math.abs(deltaT));
        }

        if (atticAir && atticAir.avgTemp > 32) {
          const temp = Math.round(atticAir.avgTemp);
          atticTemp.unshift(temp);
          if (!sensorTempMin) {
            sensorTempMin = temp;
          } else if (sensorTempMin > temp) {
            sensorTempMin = temp;
          }
          if (!sensorTempMax) {
            sensorTempMax = temp;
          } else if (sensorTempMax < temp) {
            sensorTempMax = temp;
          }
        } else {
          atticTemp.unshift(null);
        }

        if (dailyStat) {
          if (acMode === 'Heat') {
            if (dailyStat.heat > 0) {
              deltaAvgTemp.unshift(Math.abs(dailyStat.heat));
            } else {
              deltaAvgTemp.unshift(null);
            }
          } else if (dailyStat.cool > 0) {
            deltaAvgTemp.unshift(Math.abs(dailyStat.cool));
          } else {
            deltaAvgTemp.unshift(null);
          }
        }

        if (startTime) {
          cycleDate.unshift(moment(startTime).format('M/D/YY h:mm:ss a'));
        }
      });
      this.supplyTemp = supplyTemp;
      this.returnTemp = returnTemp;
      this.atticTemp = atticTemp;
      this.deltaTemp = deltaTemp;
      this.deltaAvgTemp = deltaAvgTemp;
      this.oaTemp = oaTemp;
      this.cycleDate = cycleDate;
      this.sensorTempMax = sensorTempMax;
      this.sensorTempMin = sensorTempMin;
      this.oaTempMax = oaTempMax;
      this.oaTempMin = oaTempMin;
    },
  },
  methods: {
    selectPoint(point) {
      const data = this.graphData[this.graphData.length - point.index];
      this.$store.dispatch('selectDataForExport', {
        data,
      });
    },
  },
};
</script>

<style lang="scss">
.humid-chart {
  margin-top: 16px;
  width: 100%;
  max-width: 1705px;
}
</style>
